/**
 * Outil « Tuiles algébriques » — calculs (pur).
 *
 * Un produit (ax + b)(cx + d), a et c de 0 à 3, b et d de −6 à 6, posé en
 * rectangle : côtés découpés en a tuiles « x » et |b| tuiles « 1 », chaque
 * case du rectangle est une tuile x² (x × x), x (x × 1) ou 1 (1 × 1) ;
 * rouge quand un seul des deux morceaux est négatif. Développer = compter
 * les tuiles ; factoriser = retrouver les côtés d'un rectangle qui contient
 * exactement les tuiles données (paires nulles x et −x permises).
 */

export interface Factor {
  /** coefficient de x (0 à 3) */
  a: number;
  /** constante */
  b: number;
}

export interface Poly {
  x2: number;
  x: number;
  c: number;
}

export const A_MAX = 3;
export const B_MAX = 6;

export function product(f: Factor, g: Factor): Poly {
  return { x2: f.a * g.a, x: f.a * g.b + f.b * g.a, c: f.b * g.b };
}

export const samePoly = (p: Poly, q: Poly) => p.x2 === q.x2 && p.x === q.x && p.c === q.c;

/** « 2x + 3 », « x - 2 », « -4 », « x » */
export function factorTex(f: Factor): string {
  const xs = f.a === 0 ? '' : f.a === 1 ? 'x' : `${f.a}x`;
  if (!xs) return String(f.b);
  if (f.b === 0) return xs;
  return `${xs} ${f.b < 0 ? '-' : '+'} ${Math.abs(f.b)}`;
}

/** facteur entre parenthèses quand il le faut : « (2x + 3) », « 5 », « (-2) » */
export function factorGroup(f: Factor): string {
  const t = factorTex(f);
  if (f.a !== 0 && f.b !== 0) return `(${t})`;
  return f.a === 0 && f.b < 0 ? `(${t})` : t;
}

/** le produit écrit : (2x + 3)(x + 2), 3(x - 2), x(x + 4) */
export function productTex(f: Factor, g: Factor): string {
  const isNum = (h: Factor) => h.a === 0;
  const isSum = (h: Factor) => h.a !== 0 && h.b !== 0;
  const order = (h: Factor) => (isNum(h) ? 0 : isSum(h) ? 2 : 1);
  if (isSum(f) && isSum(g))
    return f.a === g.a && f.b === g.b
      ? `(${factorTex(f)})^2`
      : `(${factorTex(f)})(${factorTex(g)})`;
  // un nombre d'abord, puis un monôme, puis une parenthèse
  const [u, v] = order(f) <= order(g) ? [f, g] : [g, f];
  if (isSum(v)) {
    const inner = `(${factorTex(v)})`;
    if (isNum(u)) return u.b === 1 ? inner : u.b === -1 ? `-${inner}` : `${u.b}${inner}`;
    return `${factorTex(u)}${inner}`;
  }
  // deux monômes ou nombres : le signe × reste écrit (« 2 × 3 », « 3 × 2x »)
  const right = isNum(v) && v.b < 0 ? `(${v.b})` : factorTex(v);
  return `${factorTex(u)} \\times ${right}`;
}

export function polyTex(p: Poly): string {
  const terms: string[] = [];
  const push = (coef: number, v: string) => {
    if (coef === 0) return;
    const abs = Math.abs(coef);
    const body = v && abs === 1 ? v : `${abs}${v}`;
    terms.push(
      terms.length === 0 ? (coef < 0 ? `-${body}` : body) : `${coef < 0 ? '-' : '+'} ${body}`,
    );
  };
  push(p.x2, 'x^2');
  push(p.x, 'x');
  push(p.c, '');
  return terms.length ? terms.join(' ') : '0';
}

/** bouts pour la feuille : 2x² + 7x + 6 → ['2','x','²','+','7','x','+','6'] */
export function polyAtoms(p: Poly): string[] {
  const out: string[] = [];
  const push = (coef: number, vars: string[]) => {
    if (coef === 0) return;
    if (out.length) out.push(coef < 0 ? '−' : '+');
    else if (coef < 0) out.push('−');
    const abs = Math.abs(coef);
    if (!(abs === 1 && vars.length)) out.push(String(abs));
    out.push(...vars);
  };
  push(p.x2, ['x', '²']);
  push(p.x, ['x']);
  push(p.c, []);
  return out.length ? out : ['0'];
}

/** un terme d'un facteur, signe compris, pour écrire les produits partiels */
function termsOf(f: Factor): Array<{ tex: string; v: 'x' | '1'; n: number }> {
  return [
    ...(f.a ? [{ tex: f.a === 1 ? 'x' : `${f.a}x`, v: 'x' as const, n: f.a }] : []),
    ...(f.b ? [{ tex: f.b < 0 ? `(${f.b})` : String(f.b), v: '1' as const, n: f.b }] : []),
  ];
}

/**
 * Les étapes du développement : produits partiels, chaque produit calculé,
 * puis la forme réduite (les tuiles x de signes contraires s'annulent).
 */
export function expandSteps(f: Factor, g: Factor): Array<{ tex: string; say: string }> {
  const tf = termsOf(f);
  const tg = termsOf(g);
  const pairs = tf.flatMap((s) => tg.map((t) => ({ s, t })));
  // chaque produit groupé : KaTeX ne coupe la ligne qu'entre deux termes
  const partial = pairs.map(({ s, t }) => `{${s.tex} \\times ${t.tex}}`).join(' + ');
  const values = pairs.map(({ s, t }) => {
    const n = s.n * t.n;
    const v = s.v === 'x' && t.v === 'x' ? 'x^2' : s.v === 'x' || t.v === 'x' ? 'x' : '';
    return { n, v };
  });
  const listed = values
    .map(({ n, v }, i) => {
      const abs = Math.abs(n);
      const body = v && abs === 1 ? v : `${abs}${v}`;
      return i === 0 ? (n < 0 ? `-${body}` : body) : `${n < 0 ? '-' : '+'} ${body}`;
    })
    .join(' ');
  const reduced = polyTex(product(f, g));
  const head = productTex(f, g);
  const lines: Array<{ tex: string; say: string }> = [];
  // « x × x = x × x » ne dit rien : on saute la ligne des produits quand elle répète l'énoncé
  if (partial.replace(/[{}]/g, '') !== head)
    lines.push({
      tex: `${head} = ${partial}`,
      say: 'On développe : chaque terme de l’un multiplie chaque terme de l’autre.',
    });
  lines.push({
    tex: lines.length ? `= ${listed}` : `${head} = ${listed}`,
    say: 'On calcule chaque produit (une case du rectangle chacun).',
  });
  if (listed !== reduced)
    lines.push({ tex: `= ${reduced}`, say: 'On réduit : on regroupe les termes en x.' });
  return lines;
}

/** combien de tuiles de chaque sorte, en comptant à part les positives et les négatives */
export function tileCounts(
  f: Factor,
  g: Factor,
): {
  x2: number;
  xPos: number;
  xNeg: number;
  onePos: number;
  oneNeg: number;
} {
  const x1 = f.a * g.b;
  const x2 = f.b * g.a;
  const xPos = Math.max(0, x1) + Math.max(0, x2);
  const xNeg = Math.max(0, -x1) + Math.max(0, -x2);
  const c = f.b * g.b;
  return {
    x2: f.a * g.a,
    xPos,
    xNeg,
    onePos: Math.max(0, c),
    oneNeg: Math.max(0, -c),
  };
}

export interface Preset {
  label: string;
  f: Factor;
  g: Factor;
  minRank: number;
}

export const PRESETS: readonly Preset[] = [
  // 5e, 4e : simple distributivité
  { label: '3(x + 2)', f: { a: 0, b: 3 }, g: { a: 1, b: 2 }, minRank: 2 },
  { label: '5(x − 1)', f: { a: 0, b: 5 }, g: { a: 1, b: -1 }, minRank: 2 },
  { label: 'x(x + 4)', f: { a: 1, b: 0 }, g: { a: 1, b: 4 }, minRank: 3 },
  { label: '−(x − 2)', f: { a: 0, b: -1 }, g: { a: 1, b: -2 }, minRank: 3 },
  { label: '3x(2x + 3)', f: { a: 3, b: 0 }, g: { a: 2, b: 3 }, minRank: 3 },
  // 3e : double distributivité, identités remarquables
  { label: '(x + 3)(x + 2)', f: { a: 1, b: 3 }, g: { a: 1, b: 2 }, minRank: 4 },
  { label: '(2x + 1)(x − 3)', f: { a: 2, b: 1 }, g: { a: 1, b: -3 }, minRank: 4 },
  { label: '(x + 3)²', f: { a: 1, b: 3 }, g: { a: 1, b: 3 }, minRank: 4 },
  { label: '(x − 2)²', f: { a: 1, b: -2 }, g: { a: 1, b: -2 }, minRank: 4 },
  { label: '(x + 4)(x − 4)', f: { a: 1, b: 4 }, g: { a: 1, b: -4 }, minRank: 4 },
];

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : Math.abs(a) || 1);

/**
 * Une factorisation est-elle finie ? « 1(3x + 6) » ne factorise rien
 * (facteur ±1) ; « 2(2x + 4) » est juste mais incomplète (2 se met encore en
 * facteur dans 2x + 4).
 */
export function factorQuality(
  f: Factor,
  g: Factor,
): { kind: 'trivial' | 'partial' | 'complete'; k?: number } {
  const unit = (h: Factor) => h.a === 0 && Math.abs(h.b) === 1;
  if (unit(f) || unit(g)) return { kind: 'trivial' };
  for (const h of [f, g]) {
    if (h.a !== 0 && h.b !== 0) {
      const k = gcd(h.a, Math.abs(h.b));
      if (k > 1) return { kind: 'partial', k };
    }
  }
  return { kind: 'complete' };
}

/**
 * Une expression à factoriser (4e : facteur commun ; 3e, 2nde : tout), avec une factorisation dans les
 * bornes de l'outil. `rng` dans [0 ; 1[.
 */
export function generateTarget(
  rng: () => number,
  rank = 4,
): { target: Poly; f: Factor; g: Factor } {
  const int = (lo: number, hi: number) => lo + Math.floor(rng() * (hi - lo + 1));
  const pick = <T>(xs: readonly T[]) => xs[int(0, xs.length - 1)]!;
  // 4e : facteur commun (un nombre, x ou kx) ; 3e : aussi les identités remarquables
  const kinds = rank <= 3 ? ['number', 'x', 'kx'] : ['number', 'x', 'kx', 'square', 'diff'];
  const kind = pick(kinds);
  let f: Factor;
  let g: Factor;
  if (kind === 'number') {
    // 3x + 6 = 3(x + 2)
    f = { a: 0, b: int(2, 5) };
    g = { a: 1, b: pick([-4, -3, -2, -1, 1, 2, 3, 4]) };
  } else if (kind === 'x') {
    // x² + 4x = x(x + 4)
    f = { a: 1, b: 0 };
    g = { a: 1, b: pick([-5, -4, -3, -2, 2, 3, 4, 5]) };
  } else if (kind === 'kx') {
    // 6x² + 9x = 3x(2x + 3), facteur complet : pgcd(2, 3) = 1
    f = { a: int(2, 3), b: 0 };
    g = pick([
      { a: 1, b: 2 },
      { a: 1, b: -3 },
      { a: 2, b: 3 },
      { a: 2, b: -1 },
      { a: 3, b: 1 },
      { a: 3, b: -2 },
    ]);
  } else if (kind === 'square') {
    // (x + 3)²
    const b = pick([-4, -3, -2, -1, 1, 2, 3, 4]);
    f = { a: 1, b };
    g = { a: 1, b };
  } else {
    // différence de deux carrés : x² − 9
    const b = int(1, 5);
    f = { a: 1, b: -b };
    g = { a: 1, b };
  }
  return { target: product(f, g), f, g };
}
